<!--
  The unread dot is a sibling of the button, not a child: `flxButton` projects an icon-only button's
  content through `<ng-content select="flx-icon,[flxIcon]" />` alone, so anything else inside is
  dropped. The filter button's dot below is placed the same way.
-->
<div class="relative flex">
  <button
    #bellButton
    [attr.aria-label]="bellAccessibleName()"
    [showDelay]="TOOLTIP_DEFAULT_DELAY"
    (click)="openPanel()"
    class="flx-ds small ternary"
    flxButton
    icon="center"
    pTooltip="Notifications"
    tooltipPosition="bottom"
    type="button"
    data-testid="notifications-bell-button"
  >
    <i
      class="ph ph-bell"
      flxIcon
    ></i>
  </button>

  @if (unreadCount() > 0) {
    <span
      class="pointer-events-none absolute right-0.5 top-0.5 size-3 rounded-full border-2 border-[color:var(--flx-header-background)] bg-blue-400"
      data-testid="notifications-bell-badge"
    ></span>
  }
</div>

<ng-template #panel>
  <div
    class="notifications-center-panel flex h-full flex-col"
    data-testid="notifications-center-panel"
  >
    <div class="flex flex-col border-b border-neutral-100">
      <div class="flex items-start justify-between gap-2 px-5 py-4">
        <span class="text-lg/7 font-bold text-neutral-900">Notifications</span>

        <div class="flex items-center gap-1">
          <button
            (click)="markAllAsRead()"
            class="flx-ds ternary small"
            flxButton
            type="button"
            data-testid="notifications-mark-all-as-read-button"
          >
            Mark all as read
          </button>

          <!-- The modals' close icon and size; the negative margin keeps the title row 28px tall. -->
          <button
            (click)="closePanel()"
            class="flx-ds neutral -my-1"
            flxButton
            icon="center"
            type="button"
            variant="text"
            data-testid="notifications-center-close-button"
            aria-label="Close notifications"
          >
            <flx-icon name="close"></flx-icon>
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2 px-5 pb-4">
        <flx-input class="flx-ds small flex-1">
          <input
            [value]="search()"
            (input)="onSearchInput($event)"
            cdkFocusInitial
            placeholder="Search notifications..."
            flxInput
            data-testid="notifications-search-input"
          />
          <i
            class="ph ph-magnifying-glass text-base/4 text-neutral-500"
            flxSuffix
          ></i>
        </flx-input>

        <div class="relative flex">
          <button
            [attr.aria-expanded]="filterOpen()"
            [attr.aria-label]="filterButtonLabel()"
            [pTooltip]="filterButtonLabel()"
            [showDelay]="TOOLTIP_DEFAULT_DELAY"
            (click)="filterOpen.set(!filterOpen())"
            class="flx-ds secondary small"
            flxButton
            icon="center"
            tooltipPosition="bottom"
            type="button"
            data-testid="notifications-filter-button"
          >
            <flx-icon [name]="isFiltering() ? 'filterActive' : 'filter'"></flx-icon>
          </button>

          @if (isFiltering()) {
            <span class="filter-applied-dot"></span>
          }

          @if (filterOpen()) {
            <!-- ds-exempt: a hand-built filter menu; moving it to a design-system popup menu is a separate change. -->
            <div
              class="absolute right-0 top-full z-10 mt-1 flex w-48 flex-col gap-0.5 rounded-lg border border-neutral-100 bg-white p-1 shadow-[2px_2px_12px_rgba(22,52,98,0.12)]"
              data-testid="notifications-filter-dropdown"
            >
              <button
                [class]="
                  filter() === NotificationFilterMode.ALL
                    ? 'bg-blue-50 text-blue-500'
                    : 'text-neutral-900 hover:bg-neutral-50'
                "
                (click)="selectFilter(NotificationFilterMode.ALL)"
                class="flex h-7 items-center rounded px-2 py-1 text-left text-xs/4"
                type="button"
                data-testid="notifications-filter-option-ALL"
              >
                All notifications
              </button>

              <button
                [class]="
                  filter() === NotificationFilterMode.UNREAD
                    ? 'bg-blue-50 text-blue-500'
                    : 'text-neutral-900 hover:bg-neutral-50'
                "
                (click)="selectFilter(NotificationFilterMode.UNREAD)"
                class="flex h-7 items-center gap-2 rounded px-2 py-1 text-left text-xs/4"
                type="button"
                data-testid="notifications-filter-option-UNREAD"
              >
                <span class="flex-1">Unread</span>
                <span
                  class="text-neutral-500"
                  data-testid="notifications-filter-unread-count"
                >
                  {{ unreadCount() }}
                </span>
              </button>

              <div class="my-1 border-t border-neutral-100"></div>

              <span class="flex h-7 items-center px-2 py-1 text-xs/4 font-semibold text-neutral-400"
                >Workspace type</span
              >

              @for (workspaceType of WORKSPACE_TYPES; track workspaceType) {
                <button
                  [class]="filter() === workspaceType ? 'bg-blue-50' : 'hover:bg-neutral-50'"
                  [attr.data-testid]="'notifications-filter-option-' + workspaceType"
                  (click)="selectFilter(workspaceType)"
                  class="flex h-7 items-center rounded px-2 py-1"
                  type="button"
                >
                  <flx-workspace-env-type-tag
                    [envType]="workspaceType"
                    [name]="workspaceType"
                  />
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>

    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      @if (loading()) {
        <div
          class="flex grow items-center justify-center py-6"
          data-testid="notifications-center-loading"
        >
          <flx-spinner size="small" />
        </div>
      } @else if (!groups().length) {
        <div class="flex grow items-center justify-center px-6 py-6 text-center">
          <p
            class="text-xs text-neutral-500"
            data-testid="notifications-center-empty"
          >
            {{ emptyMessage() }}
          </p>
        </div>
      } @else {
        @for (group of groups(); track group.period) {
          <div
            class="mt-3 px-5 pb-1 pt-1.5 text-xs/[18px] font-semibold text-neutral-400 first:mt-0"
            data-testid="notifications-center-group"
          >
            {{ group.period }}
          </div>

          @for (notification of group.notifications; track notification.id) {
            <flx-notification-center-item
              [notification]="notification"
              [currentTime]="currentTime()"
              (markedReadChange)="setMarkedRead(notification.id, $event)"
              (actionClicked)="onActionClicked(notification)"
            />
          }
        }
      }
    </div>

    <div
      class="flex h-9 shrink-0 items-center justify-center gap-1.5 border-t border-neutral-100 px-4 text-xs/4 font-semibold text-neutral-300"
      data-testid="notifications-center-footer"
    >
      <i class="ph ph-clock text-sm"></i>
      Notifications are kept for {{ NOTIFICATION_RETENTION_DAYS }} days
    </div>
  </div>
</ng-template>
